import { useRef } from 'react';

import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';

const RETARGET_WINDOW_MS = 5000;
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown'] as const;

/**
 * 섹션으로 스크롤 이동하고 searchParam에 tab 값을 추가합니다
 */
export const useScrollToSection = () => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);

  const stopRetargeting = useRef(() => {});

  const defaultOffset = isDesktopViewport ? 70 : 48;

  const scrollToSectionWithTab = (
    tab: string,
    options?: {
      behavior?: ScrollBehavior;
      offset?: number;
    },
  ) => {
    const element = document.getElementById(tab);
    if (!element) return;

    const getTop = () =>
      element.getBoundingClientRect().top +
      window.scrollY -
      defaultOffset -
      (options?.offset ?? 0);
    // iOS WebKit은 점프 스크롤 직후 한 프레임 동안 sticky 위치를 적용하지 못한다(smooth면 0회).
    const behavior = options?.behavior ?? 'smooth';

    stopRetargeting.current();
    let lastTop = getTop();
    window.scrollTo({ top: lastTop, behavior });

    // 지나가는 길의 lazy 이미지가 로드되며 목표가 밀리므로, 사용자가 직접 스크롤하기 전까지 목표를 다시 잡는다.
    const resizeObserver = new ResizeObserver(() => {
      if (!element.isConnected) {
        stop();

        return;
      }
      const top = getTop();
      if (Math.abs(top - lastTop) < 1) {
        return;
      }
      lastTop = top;
      window.scrollTo({ top, behavior });
    });
    const timeoutId = setTimeout(() => stop(), RETARGET_WINDOW_MS);
    const stop = () => {
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
      USER_SCROLL_EVENTS.forEach((eventName) =>
        window.removeEventListener(eventName, stop),
      );
      stopRetargeting.current = () => {};
    };
    resizeObserver.observe(document.body);
    USER_SCROLL_EVENTS.forEach((eventName) =>
      window.addEventListener(eventName, stop, { passive: true }),
    );
    stopRetargeting.current = stop;

    const params = new URLSearchParams(window.location.search);
    params.set('tab', tab);
    const newUrl = `${window.location.pathname}?${params.toString()}`;

    window.history.replaceState(
      { ...window.history.state, as: newUrl, url: newUrl },
      '',
      newUrl,
    );
  };

  return { scrollToSectionWithTab, defaultOffset };
};
